import { useLocation, useNavigate } from "react-router-dom";

import { Button, Checkbox, Spin } from "antd";
import { useEffect, useState } from "react";

import { DetailsCard } from "@/components/DetailsCard/DetailsCard";
import PriceSummaryModal from "@/components/PriceSummaryModal";
import SignaturePad from "@/components/SignaturePad";
import ViewPDF from "@/components/ViewPDF";
import Breadcrumbs from "@/components/common/Breadcrumb";
import { IMAGES, ROUTES_URL, SUPPORTED_COUNTRIES } from "@/constant";
import styles from "@/pages/CustomerConsent/CustomerConsent.module.css";
import { getTermAndConditions, initialteSingpassSign } from "@/services/Auth/AuthApi";
import {
  getBulkTicketPreview,
  getBulkTicketPreviewV1,
  getPledgeTicketPreview,
  provideCustomerConsent,
} from "@/services/Pledge/PledgeApis";
import { useAuthStore } from "@/stores/authStore";
import { useRenewStore } from "@/stores/renewStore";
import { usePledgesStore } from "@/stores/usePledgesStore";

const CustomerConsent: React.FC = () => {
  // const selectedPledgeData = useRenewStore((state) => state?.selectedPledgeData);
  const location = useLocation();
  const navigate = useNavigate();
  const { isSingapore } = useAuthStore();

  const [loading, setLoading] = useState(false);
  const [termsAndConditionContent, setTermsAndConditionContent] = useState("");
  const [ticketPdfUrl, setTicketPdfUrl] = useState<string | null>(null);
  const [termAndConditionCheckBox, setTermAndConditionCheckBox] = useState(false);
  const [signaturePadModal, setSignaturePadModal] = useState(false);
  const [paymentSummaryModal, setPaymentSummaryModal] = useState(false);
  const { pledges } = usePledgesStore();

  // useEffect(() => {
  //   const params = new URLSearchParams(location.search);
  //   const isSuccess = params.get("status") === "success";
  //   const pledgeId = params.get("pledge-id");

  //   if (pledgeId && isSuccess) {
  //     if (selectedPledgeData?.id) {
  //       setPaymentSummaryModal(true);
  //     } else {
  //       navigate(ROUTES_URL.PLEDGE);
  //     }
  //   }
  // }, [location, navigate]);

  const getPledgeTicketPreviewHelper = async (): Promise<void> => {
    try {
      const payload = pledges?.map((item) => ({
        pledge_id: item?.id,
        amount: item?.downloanAmount ?? 0,
        is_customer: true,
      }));
      const response = await getBulkTicketPreviewV1(payload);
      const pdfBlob = new Blob([response], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setTicketPdfUrl(`${pdfUrl}#toolbar=1&navpanes=0&scrollbar=1`);
    } catch (error) {
      console.error(error);
    }
  };

  const signatureOnSubmit = async (url: string): Promise<void> => {
    try {
      const payload = {
        pledge_ids: pledges?.map((item) => item?.id),
        action_type: "renew_pledge",
        customer_signature_media_url: url,
        consent_method: "e_signature",
        is_consent_provided: true,
      };

      await provideCustomerConsent(payload);

      setSignaturePadModal(false);

      setPaymentSummaryModal(true);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchTermAndConditions = async () => {
    try {
      setLoading(true);
      const response = await getTermAndConditions("type=create_pledge");
      const term = response?.data?.[0];
      if (term) {
        setTermsAndConditionContent(term.content || "");
      }
    } catch (error) {
      console.error("Error fetching terms:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void getPledgeTicketPreviewHelper();
    void fetchTermAndConditions();

    return (): void => {
      if (ticketPdfUrl) {
        URL.revokeObjectURL(ticketPdfUrl);
      }
    };
  }, []);

  const ticketContent = <ViewPDF pdfUrl={ticketPdfUrl ?? ""} />;

  const renderTermsAndConditions = (): JSX.Element => {
    if (loading) {
      return (
        <div className={styles.loaderWrapper}>
          <Spin spinning={loading} />
        </div>
      );
    }

    return (
      <div
        className={styles.termsContent}
        dangerouslySetInnerHTML={{ __html: termsAndConditionContent }}
      />
    );
  };

  const handleSingpassSign = async (): Promise<void> => {
    try {
      setLoading(true);
      const { signing_url } = await initialteSingpassSign({
        pledge_id: pledges?.map((item) => item?.id).join(","),
      });
      if (signing_url) {
        window.location.href = signing_url;
      }
    } catch (error) {
      console.error("Error fetching terms:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading}>
      <div className={styles.container}>
        <div className={styles.customBreadCrumbs}>
          <Breadcrumbs items={[{ label: "Renew Ticket" }]} />
        </div>

        <div className={styles.pageBody}>
          <div className={styles.previewPledgeWrapper}>
            <DetailsCard title="Pledge Ticket Preview" beforeColumns={ticketContent} />
          </div>

          <div className={styles.termsAndConditionWrapper}>
            <DetailsCard title="Terms & Conditions" beforeColumns={renderTermsAndConditions()} />
          </div>

          <Checkbox
            value={termAndConditionCheckBox}
            onChange={(e) => setTermAndConditionCheckBox(e.target.checked)}
          >
            I agree to the terms and conditions for pledge creation
          </Checkbox>

          <div className={styles.buttonWrapper}>
            {isSingapore && (
              <Button
                className={styles.dangerButton}
                onClick={() => handleSingpassSign()}
                disabled={!termAndConditionCheckBox}
              >
                <span className={styles.singpassContent}>
                  Sign with
                  <img
                    src={termAndConditionCheckBox ? IMAGES.SINGPASS : IMAGES.SINGPASS_GRAY}
                    alt="singpass"
                    className={styles.singpassLogo}
                  />
                </span>
              </Button>
            )}
            <Button
              className={styles.applyButton}
              onClick={() => setSignaturePadModal(true)}
              disabled={!termAndConditionCheckBox}
            >
              Sign with e-signature
            </Button>
          </div>
        </div>

        {signaturePadModal ? (
          <SignaturePad
            open={signaturePadModal}
            onClose={() => setSignaturePadModal(false)}
            onSubmit={(url) => {
              void signatureOnSubmit(url);
            }}
          />
        ) : null}
        {paymentSummaryModal ? (
          <PriceSummaryModal
            open={paymentSummaryModal}
            onClose={() => setPaymentSummaryModal(false)}
            onSubmit={(url) => {
              //
            }}
          />
        ) : null}
      </div>
    </Spin>
  );
};

export default CustomerConsent;
